// Schulferien: Sortierung, Filter und Datumsformatierung für Listen-Vorschau.

function asStr(v) {
  return String(v ?? "").trim();
}

/** ISO-Datum aus API-Feldern (YYYY-MM-DD oder ISO-8601) */
export function schoolHolidayYmdOnly(value) {
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(asStr(value));
  return m ? m[1] : "";
}

export function todayYmdLocal() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * @param {Array<{ id?: string, startDate?: string, endDate?: string, name?: string }>|null|undefined} holidays
 * @returns {Array<{ id: string, startDate: string, endDate: string, name: string }>}
 */
export function sortSchoolHolidaysForDisplay(holidays) {
  const list = Array.isArray(holidays) ? holidays : [];
  return [...list]
    .map((h) => ({
      id: asStr(h?.id),
      startDate: schoolHolidayYmdOnly(h?.startDate),
      endDate: schoolHolidayYmdOnly(h?.endDate),
      name: asStr(h?.name),
    }))
    .filter((h) => h.startDate && h.endDate)
    .sort(
      (a, b) =>
        a.startDate.localeCompare(b.startDate) ||
        a.endDate.localeCompare(b.endDate),
    );
}

/**
 * @param {Array<{ endDate?: string }>|null|undefined} holidays
 * @param {string} [fromYmd]
 */
export function filterUpcomingSchoolHolidays(
  holidays,
  fromYmd = todayYmdLocal(),
) {
  const from = asStr(fromYmd);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(from))
    return sortSchoolHolidaysForDisplay(holidays);
  return sortSchoolHolidaysForDisplay(holidays).filter(
    (h) => h.endDate >= from,
  );
}

/**
 * Prüft, ob ein Kalendertag in einen der Ferienzeiträume fällt (inklusive Grenzen).
 * @param {string} ymd
 * @param {Array<{ startDate?: string, endDate?: string }>|null|undefined} holidays
 * @returns {boolean}
 */
export function isYmdWithinSchoolHolidays(ymd, holidays) {
  const y = schoolHolidayYmdOnly(ymd) || asStr(ymd).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(y)) return false;
  return sortSchoolHolidaysForDisplay(holidays).some(
    (r) => y >= r.startDate && y <= r.endDate,
  );
}

/**
 * Name des Ferien-/Feiertags für ein Datum (erster Treffer).
 * @param {string} ymd
 * @param {Array<{ startDate?: string, endDate?: string, name?: string }>|null|undefined} holidays
 */
export function schoolHolidayNameForYmd(ymd, holidays) {
  const y = schoolHolidayYmdOnly(ymd) || asStr(ymd).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(y)) return "";
  const hit = sortSchoolHolidaysForDisplay(holidays).find(
    (r) => y >= r.startDate && y <= r.endDate,
  );
  return asStr(hit?.name);
}

/**
 * Schulferien für So-Training:
 * - Start am Montag → Wochenende davor (Sa/So) mitnehmen
 * - Ende Mo–Fr → bis zum folgenden Sonntag erweitern (z. B. Weihnachten bis Fr 08.01. → So 10.01.;
 *   Osterferien bis Fr 02.04. → So 04.04.)
 * - Start unter der Woche (z. B. Do 24.12.) nicht zurückziehen → So davor (20.12.) bleibt Training
 *
 * @param {Array<{ id?: string, startDate?: string, endDate?: string, name?: string }>|null|undefined} holidays
 */
export function expandSchoolHolidayRangesForWeekendSports(holidays) {
  const y = (d) => {
    const yy = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${yy}-${m}-${day}`;
  };

  return sortSchoolHolidaysForDisplay(holidays).map((h) => {
    const start = schoolHolidayYmdOnly(h.startDate);
    const end = schoolHolidayYmdOnly(h.endDate);
    if (!start || !end || start > end) return h;
    if (start === end) return h;

    const s = new Date(`${start}T12:00:00`);
    const e = new Date(`${end}T12:00:00`);
    if (!Number.isFinite(s.getTime()) || !Number.isFinite(e.getTime()))
      return h;

    const sw = s.getDay() === 0 ? 7 : s.getDay();
    const ew = e.getDay() === 0 ? 7 : e.getDay();

    // Nur bei Start Montag: Wochenende davor
    if (sw === 1) {
      s.setDate(s.getDate() - 2);
    }
    // Ende unter der Woche → bis Sonntag (auch wenn schon Sonntage im Zeitraum liegen)
    if (ew >= 1 && ew <= 5) {
      e.setDate(e.getDate() + (7 - ew));
    } else if (ew === 6) {
      e.setDate(e.getDate() + 1);
    }

    return {
      ...h,
      startDate: y(s),
      endDate: y(e),
    };
  });
}

function __ymdFromDate(d) {
  const yy = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${yy}-${m}-${day}`;
}

function __isoWeekBounds(ymd) {
  const d = new Date(`${ymd}T12:00:00`);
  if (!Number.isFinite(d.getTime())) return null;
  const dow = d.getDay() === 0 ? 7 : d.getDay();
  const mon = new Date(d);
  mon.setDate(d.getDate() - (dow - 1));
  const sun = new Date(mon);
  sun.setDate(mon.getDate() + 6);
  return { start: __ymdFromDate(mon), end: __ymdFromDate(sun) };
}

function __rangesOverlap(a0, a1, b0, b1) {
  return a0 <= b1 && b0 <= a1;
}

/**
 * Mehrtaegige Schulferien an Spieltermine anbinden: Faellt die Ferienwoche
 * auf denselben Kalenderwochen-Rahmen wie ein Spieltermin (z. B. Osterferien
 * Di–Sa → Montag derselben Woche), wird der Zeitraum auf diese Termine
 * erweitert. So erscheinen „Osterferien“ statt nur „Ostermontag“.
 *
 * @param {Array<{ id?: string, startDate?: string, endDate?: string, name?: string }>|null|undefined} holidays
 * @param {string[]} sessionDates
 */
export function expandSchoolHolidayRangesForSessionDates(
  holidays,
  sessionDates,
) {
  const sessions = (Array.isArray(sessionDates) ? sessionDates : [])
    .map((d) => schoolHolidayYmdOnly(d) || asStr(d).slice(0, 10))
    .filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d));

  return sortSchoolHolidaysForDisplay(holidays).map((h) => {
    if (h.startDate === h.endDate) return h;
    let start = h.startDate;
    let end = h.endDate;
    for (const d of sessions) {
      const week = __isoWeekBounds(d);
      if (!week) continue;
      if (!__rangesOverlap(h.startDate, h.endDate, week.start, week.end)) {
        continue;
      }
      if (d < start) start = d;
      if (d > end) end = d;
    }
    if (start === h.startDate && end === h.endDate) return h;
    return { ...h, startDate: start, endDate: end };
  });
}

/**
 * Einzeltage (oft gesetzliche Feiertage) entfernen, wenn sie bereits in einer
 * mehrtägigen Ferienperiode liegen (z. B. Ostermontag in Osterferien).
 *
 * @param {Array<{ id?: string, startDate?: string, endDate?: string, name?: string }>|null|undefined} holidays
 */
export function collapseSingleDaysInsideSchoolHolidays(holidays) {
  const list = sortSchoolHolidaysForDisplay(holidays);
  const multi = list.filter((h) => h.startDate !== h.endDate);
  if (!multi.length) return list;
  return list.filter((h) => {
    if (h.startDate !== h.endDate) return true;
    return !multi.some(
      (m) =>
        m.id !== h.id && h.startDate >= m.startDate && h.endDate <= m.endDate,
    );
  });
}

/**
 * Ferienperioden, die mindestens einen Spieltermin abdecken (nach Expand).
 *
 * @param {Array<{ id?: string, startDate?: string, endDate?: string, name?: string }>|null|undefined} holidays
 * @param {string[]} sessionDates
 */
export function schoolHolidaysTouchingSessionDates(holidays, sessionDates) {
  const sessions = (Array.isArray(sessionDates) ? sessionDates : [])
    .map((d) => schoolHolidayYmdOnly(d) || asStr(d).slice(0, 10))
    .filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d));
  const expanded = expandSchoolHolidayRangesForSessionDates(holidays, sessions);
  const collapsed = collapseSingleDaysInsideSchoolHolidays(expanded);
  return collapsed.filter((h) =>
    sessions.some((d) => d >= h.startDate && d <= h.endDate),
  );
}

/**
 * @param {string} startYmd
 * @param {string} endYmd
 * @param {string} [locale]
 */
export function formatSchoolHolidayDateRange(
  startYmd,
  endYmd,
  locale = "de-DE",
) {
  const start = asStr(startYmd);
  const end = asStr(endYmd);
  if (!start || !end) return "";
  const loc = asStr(locale) || "de-DE";
  const fmt = (ymd) => {
    try {
      const d = new Date(`${ymd}T12:00:00`);
      if (!Number.isFinite(d.getTime())) return ymd;
      return new Intl.DateTimeFormat(loc, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }).format(d);
    } catch {
      return ymd;
    }
  };
  if (start === end) return fmt(start);
  return `${fmt(start)} – ${fmt(end)}`;
}
